import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { vn } from 'vend-number'
import { T } from '@transifex/react'
import { LineItem } from '../../../models/line-item'
import { PERMISSIONS, EVENT, ERRORS } from '../../../constants'
import { FEATURE_VALIDATE_RETURN_QUANTITIES } from '../../../constants/wr-features'
import {
  useSaleInventoryData,
  useSaleManager,
  useUserPermission,
  useVdFeatureManager,
} from '../../../ng-compat/hooks'
import { $rootScope } from '../../../ng-compat/imports'
import { useAppDispatch, useAppSelector } from '../../../state/hooks'
import {
  selectIsSaleUnfulfilled,
  selectSaleHasLineItemFulfilment,
} from '../../../state/slices/currentSale/currentSaleSlice'
import { isDefinedFulfilmentType } from '../../../models/fulfilment'
import { setIsAReturnWithoutPermission } from '../../../state/slices/uiStateSlice'
import { selectSaleReturnFor } from '../../../state/selectors'
import { useSaleHasServiceOrder } from '../useSaleHasServiceOrder'
import { useSaleIsContinuedServiceSale } from '../useSaleIsContinuedServiceSale'
import { LineItemInput } from './LineItemInput'

const MAX_QUANTITY = 9999999999
const MIN_QUANTITY = -9999999999

interface Props {
  lineItem: LineItem
  editAllowedForConfirmed?: boolean
  onError: (errors: string | null) => void
  readonly?: boolean
}

export const LineItemQuantity = React.forwardRef<HTMLInputElement, Props>(
  (
    { lineItem, editAllowedForConfirmed = false, onError, readonly = false },
    ref
  ) => {
    const saleInventoryData = useSaleInventoryData()
    const isSaleUnfulfilled = useAppSelector(selectIsSaleUnfulfilled)
    const hasSaleLineItemFulfilment = useAppSelector(
      selectSaleHasLineItemFulfilment
    )
    const saleManager = useSaleManager()
    const returnPermission = useUserPermission(PERMISSIONS.SALE_RETURN)
    const dispatch = useAppDispatch()
    const saleReturnFor = useAppSelector(selectSaleReturnFor)
    const vdFeatureManager = useVdFeatureManager()
    const isValidateReturnEnabled = vdFeatureManager.get(
      FEATURE_VALIDATE_RETURN_QUANTITIES
    )

    const saleHasServiceOrder = useSaleHasServiceOrder()
    const isContinuedServiceSale = useSaleIsContinuedServiceSale()

    let min: number | string = MIN_QUANTITY
    let max = MAX_QUANTITY

    const shouldCheckQuantity =
      isSaleUnfulfilled ||
      !isDefinedFulfilmentType(lineItem.fulfilmentType) ||
      hasSaleLineItemFulfilment

    const isFulfilmentTypeSpecifiedForLIF = isDefinedFulfilmentType(
      lineItem.fulfilmentType
    )

    // If the line item quantity in the original sale was negative, it will get converted to a positive (and vice
    // versa) by the API when creating a return sale. This means that we only want to set a min / max when the new
    // lineItem quantity is less than zero.
    if (lineItem.isReturn && vn(lineItem.originalQuantity).lessThan(0)) {
      min = lineItem.originalQuantity
      max = 0
    } else if (
      !returnPermission ||
      (shouldCheckQuantity && isFulfilmentTypeSpecifiedForLIF)
    ) {
      // Set the minimum to zero if the user is not permitted to process returns and the line item is not confirmed.
      min = 0
    }

    const [error, setError] = useState<string | null>(null)
    const [inputError, setInputError] = useState<string | null>(null)
    const invalidMinError = useMemo(() => {
      const isQuantityLessThanMin = vn(lineItem.quantity).lessThan(MIN_QUANTITY)
      const isQuantityNegative = vn(lineItem.quantity).lessThan(0)

      return (
        isQuantityLessThanMin ||
        (!returnPermission && isQuantityNegative) ||
        (shouldCheckQuantity &&
          isQuantityNegative &&
          isFulfilmentTypeSpecifiedForLIF)
      )
    }, [
      returnPermission,
      shouldCheckQuantity,
      lineItem.quantity,
      isFulfilmentTypeSpecifiedForLIF,
    ])

    const returnWithoutPermission = useMemo(() => {
      const isQuantityNegative = vn(lineItem.quantity).lessThan(0)

      return !returnPermission && isQuantityNegative
    }, [returnPermission, lineItem.quantity])

    const checkQuantity = useCallback(() => {
      dispatch(setIsAReturnWithoutPermission(returnWithoutPermission))

      // Only check inventory when fulfillment type hasn't been selected yet,
      // or when the sale is unfulfilled without line item fulfillment
      const shouldCheckFulfillmentConditions =
        !isDefinedFulfilmentType(lineItem.fulfilmentType) ||
        (!isSaleUnfulfilled && !hasSaleLineItemFulfilment)

      // Show out-of-stock error when item has no inventory and fulfillment conditions apply.
      // For service sales: new ones skip this check (handled by JAWS, the service order microservice),
      // continued ones always show the error so user can choose to proceed anyway.
      const shouldShowInventoryError =
        (isContinuedServiceSale ||
          (!saleHasServiceOrder && shouldCheckFulfillmentConditions)) &&
        saleInventoryData.shouldValidateInventory(lineItem) &&
        !saleInventoryData.hasInventory(lineItem)

      if (shouldShowInventoryError) {
        setError(ERRORS.NO_INVENTORY)
        onError(ERRORS.NO_INVENTORY)
      } else if (vn(lineItem.quantity).greaterThan(MAX_QUANTITY)) {
        setError(ERRORS.INVALID_MAX)
        onError(ERRORS.INVALID_MAX)
      } else if (
        isValidateReturnEnabled &&
        saleReturnFor &&
        !lineItem.isReturn &&
        vn(lineItem.quantity).lessThan(0)
      ) {
        // In a referenced return sale, newly-added items cannot have negative qty
        setError(ERRORS.INVALID_UNREFERENCED_RETURN)
        onError(ERRORS.INVALID_UNREFERENCED_RETURN)
      } else if (invalidMinError) {
        setError(ERRORS.INVALID_MIN)
        onError(ERRORS.INVALID_MIN)
      } else if (inputError) {
        if (inputError === ERRORS.INVALID_MIN && lineItem.isReturn) {
          setError(ERRORS.INVALID_MIN_RETURN_QUANTITY)
          onError(ERRORS.INVALID_MIN_RETURN_QUANTITY)
        } else if (inputError === ERRORS.INVALID_MAX && lineItem.isReturn) {
          setError(ERRORS.INVALID_MAX_RETURN_QUANTITY)
          onError(ERRORS.INVALID_MAX_RETURN_QUANTITY)
        } else {
          setError(inputError)
          onError(inputError)
        }
      } else {
        setError(null)
        onError(null)
      }
    }, [
      isSaleUnfulfilled,
      hasSaleLineItemFulfilment,
      saleInventoryData,
      lineItem,
      invalidMinError,
      inputError,
      onError,
      returnWithoutPermission,
      dispatch,
      saleHasServiceOrder,
      isContinuedServiceSale,
      saleReturnFor,
      isValidateReturnEnabled,
    ])

    useEffect(() => {
      saleInventoryData.on(EVENT.LINE_ITEM_UPDATE_VALIDATION, checkQuantity)

      return () => {
        saleInventoryData.off(EVENT.LINE_ITEM_UPDATE_VALIDATION, checkQuantity)
      }
    }, [checkQuantity, lineItem, onError, saleInventoryData])

    useEffect(() => {
      checkQuantity()
    }, [checkQuantity])

    const disabled =
      readonly ||
      (lineItem.confirmed && !editAllowedForConfirmed) ||
      (lineItem.isReturn &&
        vn(lineItem.originalQuantity).lessThan(0) &&
        !returnPermission)

    const onChange = useCallback(
      (value: string) => {
        saleManager.updateLineItemQuantity({
          lineItem: {
            ...lineItem,
            displayQuantity: value,
          },
        })
      },
      [lineItem, saleManager]
    )

    const onBlur = useCallback(
      (value: string) => {
        // For workflows
        $rootScope.$emit('sale:update-line-item', lineItem)
      },
      [lineItem]
    )

    return (
      <div>
        <div className="vd-field li-cell-actions-field">
          <div className="vd-label">
            <T _str="Quantity" />
          </div>
          <div className="vd-value li-cell-actions-value">
            {disabled ? (
              <div>{lineItem.displayQuantity}</div>
            ) : (
              <LineItemInput
                className="li-input--quantity"
                decimalPlaces={3}
                error={error}
                max={max}
                min={min}
                onError={setInputError}
                onValueChange={onChange}
                onFocusChange={onBlur}
                ref={ref}
                trimInsignificantDigits
                value={lineItem.quantity}
              />
            )}
          </div>
        </div>
      </div>
    )
  }
)
LineItemQuantity.displayName = 'LineItemQuantity'
